import React, { useState, useEffect } from 'react';
import { BRAND } from '../config/region';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Play, Edit2, Trash2, Send, MessageCircle } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../utils/apiUtils';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { t } from '../i18n';
import ParticleBackground from './ParticleBackground';
import SeoHead from './SeoHead';
import Modal from './Modal';
import './ProductionNovelPlay.css';

const COMMENTS_PER_PAGE = 6;

const COMMENT_CLAMP = 5;

const CommentText = ({ text, citedComment }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight);
  }, []);
  return (
    <>
      {citedComment && (
        <div className="pnp-cite-block">
          <span className="pnp-cite-author">@{citedComment.username}</span>
          <span className="pnp-cite-text">{citedComment.text}</span>
        </div>
      )}
      <p
        ref={ref}
        className="pnp-comment-text"
        style={expanded ? {} : { display: '-webkit-box', WebkitLineClamp: COMMENT_CLAMP, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >{text}</p>
      {isClamped && !expanded && (
        <button className="pnp-read-more" onClick={() => setExpanded(true)}>{t('Читать далее')}</button>
      )}
    </>
  );
};

const ProductionNovelPlay = () => {
  const { playLink } = useParams();
  const navigate = useNavigate();

  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [likes, setLikes] = useState(0);
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [totalComments, setTotalComments] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState(null);
  const [likingComment, setLikingComment] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // { id, username }
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({}); // { commentId: replies[] | 'loading' }

  useEffect(() => { fetchNovel(); }, [playLink]);
  useEffect(() => { if (novel?.id) fetchComments(); }, [novel?.id]);

  const fetchNovel = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`${API_URLS.PRODUCTION_NOVELS}/${playLink}`);
      setNovel(response.data);
      setLikes(response.data.likes || 0);
      setUserHasLiked(response.data.user_has_liked || false);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? t('Новелла не найдена') : t('Ошибка загрузки'));
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (liking || !novel) return;
    try {
      setLiking(true);
      const response = await apiPost(API_URLS.PRODUCTION_NOVEL_LIKE(novel.id), {});
      setUserHasLiked(response.data.has_liked);
      setLikes(response.data.likes_count);
    } catch (err) {
      if (err.message?.includes('Only registered users')) {
        setModalMessage(t('Только зарегистрированные пользователи могут лайкать новеллы.'));
        setShowModal(true);
      }
    } finally {
      setLiking(false);
    }
  };

  const fetchComments = async (page = 1) => {
    try {
      setCommentsLoading(true);
      const offset = (page - 1) * COMMENTS_PER_PAGE;
      const response = await apiGet(
        `${API_URLS.PRODUCTION_NOVEL_COMMENTS(novel.id)}?limit=${COMMENTS_PER_PAGE}&offset=${offset}`
      );
      setComments(response.data.comments || []);
      setTotalComments(response.data.total_comments || 0);
      setCommentsPage(page);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;
    try {
      setSubmittingComment(true);
      const response = await apiPost(API_URLS.PRODUCTION_NOVEL_COMMENTS(novel.id), { text: newComment.trim() });
      setNewComment('');
      setNovel(prev => prev ? { ...prev, number_of_comments: prev.number_of_comments + 1 } : null);
      await fetchComments(1);
    } catch (err) {
      if (err.message?.includes('Only registered users')) {
        setModalMessage(t('Только зарегистрированные пользователи могут оставлять комментарии.'));
        setShowModal(true);
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (likingComment === commentId) return;
    try {
      setLikingComment(commentId);
      const res = await apiPost(API_URLS.PRODUCTION_NOVEL_COMMENT_LIKE(novel.id, commentId));
      const { likes, is_liked } = res.data;
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, likes, is_liked } : c));
      setExpandedReplies(prev => {
        const updated = {};
        for (const [parentId, replies] of Object.entries(prev)) {
          updated[parentId] = Array.isArray(replies)
            ? replies.map(r => r.id === commentId ? { ...r, likes, is_liked } : r)
            : replies;
        }
        return updated;
      });
    } catch (err) {
      if (err.message?.includes('Only registered users') || err.message?.includes('registered')) {
        setModalMessage(t('Только зарегистрированные пользователи могут ставить лайки.'));
        setShowModal(true);
      }
    } finally {
      setLikingComment(null);
    }
  };

  const handleToggleReplies = async (commentId) => {
    if (expandedReplies[commentId]) {
      setExpandedReplies(prev => { const n = { ...prev }; delete n[commentId]; return n; });
      return;
    }
    setExpandedReplies(prev => ({ ...prev, [commentId]: 'loading' }));
    try {
      const res = await apiGet(API_URLS.PRODUCTION_NOVEL_COMMENT_REPLIES(novel.id, commentId));
      setExpandedReplies(prev => ({ ...prev, [commentId]: res.data || [] }));
    } catch (err) {
      console.error('Error fetching replies:', err);
      setExpandedReplies(prev => { const n = { ...prev }; delete n[commentId]; return n; });
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || submittingReply || !replyingTo) return;
    try {
      setSubmittingReply(true);
      const res = await apiPost(API_URLS.PRODUCTION_NOVEL_COMMENTS(novel.id), {
        text: replyText.trim(),
        parent_comment_id: replyingTo.id,
        cited_comment_id: replyingTo.citedCommentId ?? null,
      });
      setReplyText('');
      setReplyingTo(null);
      setNovel(prev => prev ? { ...prev, number_of_comments: prev.number_of_comments + 1 } : null);
      setComments(prev => prev.map(c => c.id === replyingTo.id ? { ...c, reply_count: (c.reply_count || 0) + 1 } : c));
      setExpandedReplies(prev => ({
        ...prev,
        [replyingTo.id]: Array.isArray(prev[replyingTo.id]) ? [...prev[replyingTo.id], res.data] : [res.data],
      }));
    } catch (err) {
      if (err.message?.includes('Only registered users')) {
        setModalMessage(t('Только зарегистрированные пользователи могут отвечать на комментарии.'));
        setShowModal(true);
      }
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDeleteReply = async (replyId, parentId) => {
    if (deletingComment === replyId) return;
    try {
      setDeletingComment(replyId);
      await apiDelete(API_URLS.PRODUCTION_NOVEL_COMMENT_DELETE(novel.id, replyId));
      setNovel(prev => prev ? { ...prev, number_of_comments: Math.max(0, prev.number_of_comments - 1) } : null);
      setComments(prev => prev.map(c => c.id === parentId ? { ...c, reply_count: Math.max(0, (c.reply_count || 0) - 1) } : c));
      setExpandedReplies(prev => ({
        ...prev,
        [parentId]: Array.isArray(prev[parentId]) ? prev[parentId].filter(r => r.id !== replyId) : prev[parentId],
      }));
    } catch (err) {
      console.error('Error deleting reply:', err);
    } finally {
      setDeletingComment(null);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (deletingComment === commentId) return;
    try {
      setDeletingComment(commentId);
      await apiDelete(API_URLS.PRODUCTION_NOVEL_COMMENT_DELETE(novel.id, commentId));
      setNovel(prev => prev ? { ...prev, number_of_comments: Math.max(0, prev.number_of_comments - 1) } : null);
      await fetchComments(commentsPage);
    } catch (err) {
      console.error('Error deleting comment:', err);
    } finally {
      setDeletingComment(null);
    }
  };

  const handleDeleteNovel = async () => {
    if (!window.confirm(t('Удалить новеллу? Это действие нельзя отменить.'))) return;
    try {
      setDeleting(true);
      await apiDelete(`${API_URLS.PRODUCTION_NOVELS}/${novel.id}`);
      navigate(-1);
    } catch (err) {
      setError(t('Ошибка при удалении новеллы.'));
      setDeleting(false);
    }
  };

  const searchReturnUrl = sessionStorage.getItem('searchReturnUrl') || '/search';

  const coverUrl = novel?.cover_background_image_id && novel?.cover_background_image_extension
    ? `${GCS_BUCKET_URL}/production/${novel.id}/background/${novel.cover_background_image_id}.${novel.cover_background_image_extension}${novel.cover_background_image_cache_id ? `?cache_id=${novel.cover_background_image_cache_id}` : ''}`
    : null;

  const busyText = loading ? t('Загрузка…') : t('Удаление…');

  if (loading || deleting) {
    return (
      <div className="pnp-wrap">
        <ParticleBackground />
        <div className="pnp-loading">
          <div className="pnp-spinner" />
          <span>{busyText}</span>
        </div>
      </div>
    );
  }

  if (error && !novel) {
    return (
      <div className="pnp-wrap">
        <ParticleBackground />
        <div className="pnp-error-page">
          <p>{error}</p>
          <a className="pnp-btn-back" href={sessionStorage.getItem('searchReturnUrl') || '/search'}>
            <ChevronLeft size={16} /> {t('Поиск')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pnp-wrap">
      <ParticleBackground />

      {novel && (
        <SeoHead
          title={novel.name}
          description={novel.description || t('Играй в интерактивную новеллу «{0}» с ИИ-персонажами на {1}.', novel.name, BRAND)}
          path={`/production/${playLink}`}
          image={coverUrl || undefined}
          imageAlt={coverUrl ? novel.name : undefined}
          type="article"
        />
      )}

      <div className="pnp-app">

        {/* Floating header over cover */}
        <div className="pnp-header">
          <a className="pnp-back" href={searchReturnUrl}>
            <ChevronLeft size={14} /> {t('Поиск')}
          </a>
          {(novel?.is_owner || novel?.is_admin) && (
            <div className="pnp-owner-actions">
              <a className="pnp-btn-edit" href={`/production_change/${novel.id}`}>
                <Edit2 size={14} />
              </a>
              <button className="pnp-btn-delete" onClick={handleDeleteNovel}>
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Cover image */}
        {coverUrl && (
          <div className="pnp-cover">
            <img src={coverUrl} alt={novel.name} className="pnp-cover-img" />
            <div className="pnp-cover-fade" />
          </div>
        )}

        {/* Info section */}
        <div className={`pnp-info${coverUrl ? '' : ' pnp-info--no-cover'}`}>
          <h1 className="pnp-title">{novel.name}</h1>
          <p className="pnp-date">
            {new Date(novel.created_at).toLocaleDateString('ru-RU', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
          </p>

          <div className="pnp-like-row">
            <button
              className={`pnp-like-btn${userHasLiked ? ' pnp-like-btn--active' : ''}${liking ? ' pnp-like-btn--loading' : ''}`}
              onClick={handleLike}
              disabled={liking}
            >
              <Heart size={15} fill={userHasLiked ? 'currentColor' : 'none'} />
              {likes}
              <span className="pnp-like-label">{userHasLiked ? t('Убрать лайк') : t('Нравится')}</span>
            </button>
          </div>

          {novel.description && <p className="pnp-desc">{novel.description}</p>}
          {novel.technical_description && <p className="pnp-tech-desc">{novel.technical_description}</p>}

          <div className="pnp-actions">
            <a className="pnp-btn-play" href={`/play/${playLink}`}>
              <Play size={18} fill="currentColor" /> {t('Играть')}
            </a>
          </div>
        </div>

        {/* Comments */}
        <div className="pnp-comments">
          <h2 className="pnp-comments-title">
            <MessageCircle size={16} />
            {t('Комментарии')}
            {novel?.number_of_comments > 0 && (
              <span className="pnp-comments-count">{novel.number_of_comments}</span>
            )}
          </h2>

          <form className="pnp-comment-form" onSubmit={handleSubmitComment}>
            <textarea
              className="pnp-comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t('Напишите комментарий…')}
              rows={3}
              maxLength={1000}
              disabled={submittingComment}
            />
            <button
              type="submit"
              className="pnp-comment-submit"
              disabled={!newComment.trim() || submittingComment}
            >
              <Send size={13} />
              {submittingComment ? t('Отправка…') : t('Отправить')}
            </button>
          </form>

          <div className="pnp-comments-list">
            {commentsLoading ? (
              <div className="pnp-spinner pnp-spinner--sm" />
            ) : comments.length === 0 ? (
              <p className="pnp-no-comments">{t('Пока нет комментариев. Будьте первым!')}</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="pnp-comment">
                  <div className="pnp-comment-header">
                    <strong className="pnp-comment-user">{comment.username}</strong>
                    <span className="pnp-comment-date">
                      {new Date(comment.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                  <CommentText text={comment.text} citedComment={comment.cited_comment} />
                  <div className="pnp-comment-actions">
                    <button
                      className={`pnp-comment-like${comment.is_liked ? ' pnp-comment-like--active' : ''}${likingComment === comment.id ? ' pnp-comment-like--loading' : ''}`}
                      onClick={() => handleLikeComment(comment.id)}
                      disabled={likingComment === comment.id}
                    >
                      ♥ {comment.likes || 0}
                    </button>
                    <button
                      className="pnp-comment-reply-btn"
                      onClick={() => setReplyingTo(replyingTo?.id === comment.id && !replyingTo?.citedCommentId ? null : { id: comment.id, username: comment.username })}
                    >
                      {t('Ответить')}
                    </button>
                    {(comment.is_owner || novel?.is_admin) && (
                      <button
                        className="pnp-comment-delete"
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingComment === comment.id}
                      >
                        {deletingComment === comment.id ? t('Удаление…') : t('Удалить')}
                      </button>
                    )}
                  </div>

                  {replyingTo?.id === comment.id && !replyingTo?.citedCommentId && (
                    <form className="pnp-reply-form" onSubmit={handleSubmitReply}>
                      <textarea
                        className="pnp-comment-input pnp-reply-input"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={t('Ответить {0}…', replyingTo.username)}
                        rows={2}
                        maxLength={1000}
                        disabled={submittingReply}
                        autoFocus
                      />
                      <div className="pnp-reply-form-actions">
                        <button type="submit" className="pnp-comment-submit" disabled={!replyText.trim() || submittingReply}>
                          <Send size={13} /> {submittingReply ? t('Отправка…') : t('Ответить')}
                        </button>
                        <button type="button" className="pnp-reply-cancel" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                          {t('Отмена')}
                        </button>
                      </div>
                    </form>
                  )}

                  {comment.reply_count > 0 && (
                    <button className="pnp-show-replies" onClick={() => handleToggleReplies(comment.id)}>
                      {expandedReplies[comment.id] ? t('▲ Скрыть ответы') : t('▼ Показать ответы ({0})', comment.reply_count)}
                    </button>
                  )}

                  {expandedReplies[comment.id] && (
                    <div className="pnp-replies">
                      {expandedReplies[comment.id] === 'loading' ? (
                        <div className="pnp-spinner pnp-spinner--sm" />
                      ) : (
                        expandedReplies[comment.id].map(reply => (
                          <div key={reply.id} className="pnp-reply">
                            <div className="pnp-comment-header">
                              <strong className="pnp-comment-user">{reply.username}</strong>
                              <span className="pnp-comment-date">{new Date(reply.created_at).toLocaleDateString('ru-RU')}</span>
                            </div>
                            <CommentText text={reply.text} citedComment={reply.cited_comment} />
                            <div className="pnp-comment-actions">
                              <button
                                className={`pnp-comment-like${reply.is_liked ? ' pnp-comment-like--active' : ''}${likingComment === reply.id ? ' pnp-comment-like--loading' : ''}`}
                                onClick={() => handleLikeComment(reply.id)}
                                disabled={likingComment === reply.id}
                              >
                                ♥ {reply.likes || 0}
                              </button>
                              <button
                                className="pnp-comment-reply-btn"
                                onClick={() => setReplyingTo(replyingTo?.citedCommentId === reply.id ? null : { id: comment.id, username: reply.username, citedCommentId: reply.id })}
                              >
                                {t('Ответить')}
                              </button>
                              {(reply.is_owner || novel?.is_admin) && (
                                <button
                                  className="pnp-comment-delete"
                                  onClick={() => handleDeleteReply(reply.id, comment.id)}
                                  disabled={deletingComment === reply.id}
                                >
                                  {deletingComment === reply.id ? t('Удаление…') : t('Удалить')}
                                </button>
                              )}
                            </div>
                            {replyingTo?.citedCommentId === reply.id && (
                              <form className="pnp-reply-form" onSubmit={handleSubmitReply}>
                                <div className="pnp-cite-block">
                                  <span className="pnp-cite-author">@{replyingTo.username}</span>
                                  <span className="pnp-cite-text">{reply.text}</span>
                                </div>
                                <textarea
                                  className="pnp-comment-input pnp-reply-input"
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder={t('Ответить {0}…', replyingTo.username)}
                                  rows={2}
                                  maxLength={1000}
                                  disabled={submittingReply}
                                  autoFocus
                                />
                                <div className="pnp-reply-form-actions">
                                  <button type="submit" className="pnp-comment-submit" disabled={!replyText.trim() || submittingReply}>
                                    <Send size={13} /> {submittingReply ? t('Отправка…') : t('Ответить')}
                                  </button>
                                  <button type="button" className="pnp-reply-cancel" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                                    {t('Отмена')}
                                  </button>
                                </div>
                              </form>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {Math.ceil(totalComments / COMMENTS_PER_PAGE) > 1 && (
            <div className="pnp-c-pagination">
              <button
                className="pnp-c-page-btn"
                onClick={() => fetchComments(commentsPage - 1)}
                disabled={commentsPage === 1 || commentsLoading}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="pnp-c-page-info">
                {commentsPage} / {Math.ceil(totalComments / COMMENTS_PER_PAGE)}
              </span>
              <button
                className="pnp-c-page-btn"
                onClick={() => fetchComments(commentsPage + 1)}
                disabled={commentsPage === Math.ceil(totalComments / COMMENTS_PER_PAGE) || commentsLoading}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="pnp-spacer" />
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={t('Ошибка')}
        message={modalMessage}
      />
    </div>
  );
};

export default ProductionNovelPlay;
